import { useRef, useState, useCallback, useEffect } from "react";
import { loadStyles } from "../../../utils/cssLoader";
import { formatRssDate } from "../../../utils/interactivesHelper";
import NextImage from "../../common/NextImage";

/**
 * TwitterCard — renders a tweet-style card with inline muted video preview.
 * Video autoplays muted when 30%+ visible; pauses when scrolled away.
 */
const TwitterCard = ({ item = {}, loadCSS = true, onClick = null }) => {
  const { description, pubDate, image, video, author, authorImage } = item;
  const videoElRef = useRef(null); // keeps a direct reference to the <video> DOM node
  const obsRef = useRef(null);     // keeps the IntersectionObserver instance
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (loadCSS === true || loadCSS === "true") {
      loadStyles("interactives");
    }
  }, [loadCSS]);

  // Callback ref: fires synchronously when the <video> mounts or unmounts.
  const setVideoRef = useCallback((el) => {
    videoElRef.current = el;

    // Clean up any previous observer
    if (obsRef.current) {
      obsRef.current.disconnect();
      obsRef.current = null;
    }
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.play().catch(() => {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    obsRef.current = obs;
  }, []); // stable — no deps needed

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoElRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const text = description || item.title || "";
  if (!text && !video && !image) return null;

  const hasMedia = Boolean(video || image);

  // ── Text-only: dark gradient bg with floating white tweet card ──
  if (!hasMedia) {
    return (
      <div className="tw-card tw-card--text-only" onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
        <div className="tw-card__text-bg">
          <div className="tw-card__text-inner">
            <span className="tw-card__logo-abs">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </span>
            <div className="tw-card__header">
              {authorImage ? (
                <img src={authorImage} alt={author || ""} className="tw-card__avatar tw-card__avatar--lg" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : (
                <div className="tw-card__avatar tw-card__avatar--lg tw-card__avatar--placeholder">🐦</div>
              )}
              <div className="tw-card__meta">
                {author && <span className="tw-card__name">{author}</span>}
                {pubDate && <span className="tw-card__date">{formatRssDate(pubDate)}</span>}
              </div>
            </div>
            {text && <p className="tw-card__text">{text}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tw-card">
      {/* Media — never opens slider; video + mute stay fully interactive */}
      {video ? (
        <div className="tw-card__media">
          <video
            ref={setVideoRef}
            src={video}
            className="tw-card__video"
            muted
            loop
            playsInline
            preload="metadata"
            poster={image || undefined}
          />
          <button
            className="tw-card__mute-btn"
            onClick={toggleMute}
            aria-label={muted ? "Unmute" : "Mute"}
          >
            {muted ? "🔇" : "🔊"}
          </button>
        </div>
      ) : (
        <div
          className="tw-card__media tw-card__media--image"
          onClick={onClick}
          style={onClick ? { cursor: "pointer" } : undefined}
        >
          <NextImage
            src={image}
            alt={text}
            clz="tw-card__img"
            fill
            objectFit="cover"
          />
        </div>
      )}

      {/* Caption pinned to bottom — clicking opens slider */}
      <div
        className="tw-card__caption-wrap"
        onClick={onClick}
        style={onClick ? { cursor: "pointer" } : undefined}
      >
        <div className="tw-card__header">
          {authorImage ? (
            <img
              src={authorImage}
              alt={author || ""}
              className="tw-card__avatar"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="tw-card__avatar tw-card__avatar--placeholder">🐦</div>
          )}
          <div className="tw-card__meta">
            {author && <span className="tw-card__name">{author}</span>}
            {pubDate && (
              <span className="tw-card__date">{formatRssDate(pubDate)}</span>
            )}
          </div>
          <span className="tw-card__logo">𝕏</span>
        </div>

        {text && <p className="tw-card__text">{text}</p>}
      </div>
    </div>
  );
};

export default TwitterCard;
